// src/features/search/SearchDropdown.jsx
// Desktop search dropdown — hidden on mobile (md:block).
//
// Sections (in order):
//   1. Recent searches  (when query is empty)
//   2. Trending         (when query is empty)
//   3. Categories       (when query has results)
//   4. Keyword suggestions (when query has results)
//   5. Products         (always when query has results)
//   6. Zero-result state: "Did you mean X?" + similar products
//   7. "View all" footer

import { useEffect, useRef } from 'react';
import { Link }               from 'react-router-dom';
import { Search, ArrowRight, RefreshCw, Clock, TrendingUp, X, Tag, Sparkles } from 'lucide-react';
import { formatPrice }        from '@/lib/utils';

// ── Skeleton ──────────────────────────────────────────────────────────────────
function Skeleton() {
  return (
    <div className="p-4 space-y-3">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="flex items-center gap-3 animate-pulse">
          <div className="w-11 h-11 rounded-md bg-gray-200 shrink-0" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3 bg-gray-200 rounded w-3/4" />
            <div className="h-2.5 bg-gray-200 rounded w-1/3" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Product row ───────────────────────────────────────────────────────────────
function ProductRow({ product, onClose }) {
  const hasDiscount = product.compare_at_price && product.compare_at_price > product.price;
  return (
    <Link
      to={`/product/${product.id}`}
      onClick={onClose}
      className="flex items-center gap-3 px-4 py-2.5 hover:bg-[#FAF8F3] transition-colors"
    >
      <div className="w-11 h-11 rounded-md bg-gray-100 shrink-0 overflow-hidden">
        {product.thumbnail ? (
          <img src={product.thumbnail} alt={product.name}
               className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Search className="w-4 h-4 text-gray-300" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-900 truncate">{product.name}</p>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-sm font-bold text-[#D4AF37]">
            ₹{Number(product.price).toLocaleString('en-IN')}
          </span>
          {hasDiscount && (
            <span className="text-xs text-gray-400 line-through">
              ₹{Number(product.compare_at_price).toLocaleString('en-IN')}
            </span>
          )}
          {product.discount_percent > 0 && (
            <span className="text-[10px] font-semibold bg-red-100 text-red-600 px-1 py-0.5 rounded">
              {product.discount_percent}% OFF
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

// ── Section heading ───────────────────────────────────────────────────────────
function SectionHeading({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-1.5 px-4 pt-3 pb-1">
      <Icon className="w-3.5 h-3.5 text-gray-400" />
      <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">
        {label}
      </span>
    </div>
  );
}

// ── Pill chip ─────────────────────────────────────────────────────────────────
function Chip({ label, onClick, onDelete }) {
  return (
    <button
      onClick={onClick}
      className="group inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-gray-100
                 hover:bg-[#FAF8F3] text-gray-700 rounded-full transition-colors"
    >
      {label}
      {onDelete && (
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          onKeyDown={(e) => e.key === 'Enter' && (e.stopPropagation(), onDelete())}
          className="ml-0.5 text-gray-400 hover:text-gray-700"
        >
          <X className="w-3 h-3" />
        </span>
      )}
    </button>
  );
}

// ── SearchDropdown ────────────────────────────────────────────────────────────
export function SearchDropdown({
  query, setQuery,
  products, categories, suggestions,
  loading, error,
  hasQuery, hasResults,
  isOpen, close, retry, onSubmit, onSelectSuggestion,
  recent, deleteRecent, clearRecent,
  trending,
  didYouMean, similarProducts,
}) {
  const inputRef     = useRef(null);
  const containerRef = useRef(null);

  // Autofocus
  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 50);
  }, [isOpen]);

  // ESC closes
  useEffect(() => {
    if (!isOpen) return;
    const h = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [isOpen, close]);

  // Click outside closes — desktop only.
  //
  // SearchDropdown and SearchOverlay share the same isOpen state. On mobile,
  // SearchDropdown is CSS-hidden (hidden md:block) but still mounted, so its
  // document-level mousedown listener was firing for every tap inside
  // SearchOverlay. Because mousedown fires before click, close() was called
  // before clearRecent/onSelectSuggestion could run — unmounting SearchOverlay
  // and swallowing the tap entirely.
  //
  // Fix: bail out when the container element is not visible (display === 'none'),
  // which is the case on mobile where Tailwind's 'hidden md:block' applies.
  useEffect(() => {
    if (!isOpen) return;
    const h = (e) => {
      if (!containerRef.current) return;
      // 'hidden md:block' sets display:none on mobile — do not process
      // click-outside events when the dropdown is not actually visible.
      if (window.getComputedStyle(containerRef.current).display === 'none') return;
      if (!containerRef.current.contains(e.target)) close();
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [isOpen, close]);

  if (!isOpen) return null;

  const showIdle    = !hasQuery;
  const showResults = hasQuery && !loading;

  return (
    <div
      ref={containerRef}
      style={{ top: 'var(--navbar-h)' }}
      className="fixed left-0 w-full bg-white border-b border-gray-100 shadow-2xl z-40 hidden md:block"
    >
      <div className="max-w-[1400px] mx-auto px-6 py-4">

        {/* ── Input ── */}
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') onSubmit(); }}
            placeholder="Search necklaces, earrings, rings…"
            className="w-full pl-12 pr-28 py-3.5 text-base border-2 border-gray-200 rounded-sm
                       focus:outline-none focus:border-[#D4AF37] transition-colors"
          />
          {hasQuery && (
            <button
              onClick={onSubmit}
              className="absolute right-3 top-1/2 -translate-y-1/2 px-4 py-1.5 text-sm font-semibold
                         bg-[#D4AF37] text-[#0A0A0A] rounded-sm hover:bg-[#B5952F] transition-colors"
            >
              Search
            </button>
          )}
        </div>

        {/* ── Panel ── */}
        <div className="mt-2 border border-gray-100 rounded-sm overflow-hidden">

          {/* ── IDLE: Recent + Trending ── */}
          {showIdle && (
            <div className="py-1">
              {recent.length > 0 && (
                <>
                  <div className="flex items-center justify-between px-4 pt-3 pb-1">
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-gray-400" />
                      <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">
                        Recent
                      </span>
                    </div>
                    <button
                      onClick={clearRecent}
                      className="text-[10px] text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      Clear all
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2 px-4 pb-3">
                    {recent.map((r) => (
                      <Chip
                        key={r}
                        label={r}
                        onClick={() => onSelectSuggestion(r)}
                        onDelete={() => deleteRecent(r)}
                      />
                    ))}
                  </div>
                </>
              )}

              {trending.length > 0 && (
                <>
                  <SectionHeading icon={TrendingUp} label="Trending" />
                  <div className="flex flex-wrap gap-2 px-4 pb-3">
                    {trending.slice(0, 8).map((t) => (
                      <Chip key={t} label={t} onClick={() => onSelectSuggestion(t)} />
                    ))}
                  </div>
                </>
              )}

              {recent.length === 0 && trending.length === 0 && (
                <p className="px-4 py-6 text-sm text-gray-400 text-center">
                  Start typing to search…
                </p>
              )}
            </div>
          )}

          {/* ── Loading ── */}
          {hasQuery && loading && <Skeleton />}

          {/* ── Error ── */}
          {hasQuery && !loading && error && (
            <div className="px-4 py-6 text-center">
              <p className="text-sm text-gray-500 mb-2">{error}</p>
              <button
                onClick={retry}
                className="inline-flex items-center gap-1.5 text-xs text-[#D4AF37] hover:underline"
              >
                <RefreshCw className="w-3 h-3" /> Retry
              </button>
            </div>
          )}

          {/* ── Empty / zero-result state ── */}
          {showResults && !error && !hasResults && (
            <div className="pb-3">
              {/* Did you mean */}
              {didYouMean ? (
                <div className="px-4 pt-4 pb-3 border-b border-gray-50">
                  <p className="text-sm text-gray-500">
                    No results for{' '}
                    <strong className="text-gray-700">"{query}"</strong>.
                    {' '}Did you mean{' '}
                    <button
                      onClick={() => onSelectSuggestion(didYouMean)}
                      className="font-semibold text-[#D4AF37] hover:underline"
                    >
                      {didYouMean}
                    </button>
                    ?
                  </p>
                </div>
              ) : (
                <p className="px-4 pt-4 pb-2 text-sm text-gray-400">
                  No results for{' '}
                  <strong className="text-gray-700">"{query}"</strong>
                </p>
              )}

              {/* Similar products */}
              {similarProducts?.length > 0 && (
                <>
                  <SectionHeading icon={Sparkles} label="You might like" />
                  <div className="divide-y divide-gray-50">
                    {similarProducts.map((p) => (
                      <ProductRow key={p.id} product={p} onClose={close} />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── Results ── */}
          {showResults && !error && hasResults && (
            <>
              {/* Categories */}
              {categories.length > 0 && (
                <>
                  <SectionHeading icon={Tag} label="Categories" />
                  <div className="flex flex-wrap gap-2 px-4 pb-3">
                    {categories.map((cat) => (
                      <Chip key={cat} label={cat} onClick={() => onSelectSuggestion(cat)} />
                    ))}
                  </div>
                </>
              )}

              {/* Keyword suggestions */}
              {suggestions.length > 0 && (
                <>
                  <SectionHeading icon={Search} label="Suggestions" />
                  <div className="pb-2">
                    {suggestions.map((s) => (
                      <button
                        key={s}
                        onClick={() => onSelectSuggestion(s)}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700
                                   hover:bg-[#FAF8F3] flex items-center gap-2 transition-colors"
                      >
                        <Search className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                        {s}
                      </button>
                    ))}
                  </div>
                </>
              )}

              {/* Products */}
              {products.length > 0 && (
                <>
                  {(categories.length > 0 || suggestions.length > 0) && (
                    <div className="border-t border-gray-50" />
                  )}
                  <SectionHeading icon={Search} label="Products" />
                  <div className="divide-y divide-gray-50">
                    {products.map((p) => (
                      <ProductRow key={p.id} product={p} onClose={close} />
                    ))}
                  </div>
                </>
              )}

              {/* View all */}
              <div className="border-t border-gray-100 px-4 py-3">
                <button
                  onClick={onSubmit}
                  className="flex items-center gap-1.5 text-xs font-semibold text-[#D4AF37]
                             hover:text-[#B5952F] transition-colors"
                >
                  View all results for "{query}"
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}